// SAM card rulebook (`sam_cards.presentation_policy`) — the shapes the editor works with, and the
// conversion between a stored rule's `when` object and editable condition rows. Every fact,
// operator, action and option name comes from the backend's `editor_schema`; nothing here lists them.
import type { RulebookFactSchema, RulebookSchema } from '@/redux/internal-services/platformAdminApi';

export interface Toast { type: 'TOAST'; level: string; message_code: number; duration_ms?: number }

export interface Decision {
  action: string;
  [option: string]: unknown;
}

export interface Rule {
  rule_id: string;
  priority: number;
  enabled?: boolean;
  description?: string;
  when: Record<string, unknown>;
  decision: Decision;
}

export interface Rulebook {
  policy_id?: string;
  version: number;
  default_decision: Decision;
  rules: Rule[];
  card_overrides?: Record<string, Rule[]>;
}

export type Operator = 'equals' | 'any_of' | 'not_in' | 'exists' | 'contains';

export interface ConditionRow {
  fact: string;
  op: Operator;
  /** equals → string | boolean | number; any_of / not_in / contains → string[]; exists → boolean */
  value: unknown;
}

/** Which operators make sense for a fact, in the order the picker shows them. */
export function operatorsFor(fact: RulebookFactSchema | undefined, allowed: string[]): Operator[] {
  const byKind: Record<RulebookFactSchema['kind'], Operator[]> = {
    enum: ['equals', 'any_of', 'not_in', 'exists'],
    card: ['equals', 'any_of', 'not_in', 'exists'],
    bool: ['equals', 'exists'],
    int: ['equals'],
    text: ['equals', 'exists'],
    cards: ['contains'],
  };
  return (fact ? byKind[fact.kind] : (['equals'] as Operator[])).filter((o) => allowed.includes(o));
}

/** The values a fact can take (cards come from the catalogue list). */
export function valuesFor(fact: RulebookFactSchema | undefined, schema: RulebookSchema): string[] {
  if (!fact) return [];
  if (fact.kind === 'card' || fact.kind === 'cards') return schema.cards;
  return fact.values ?? [];
}

export function defaultValueFor(fact: RulebookFactSchema | undefined, op: Operator, schema: RulebookSchema): unknown {
  if (op === 'exists') return true;
  if (op === 'any_of' || op === 'not_in' || op === 'contains') return [];
  if (!fact) return '';
  if (fact.kind === 'bool') return true;
  if (fact.kind === 'int') return 0;
  return valuesFor(fact, schema)[0] ?? '';
}

export function whenToRows(when: Record<string, unknown>): ConditionRow[] {
  return Object.entries(when || {}).map(([fact, cond]) => {
    if (Array.isArray(cond)) return { fact, op: 'any_of', value: cond.map(String) };
    if (cond && typeof cond === 'object') {
      const c = cond as Record<string, unknown>;
      if ('not_in' in c) return { fact, op: 'not_in', value: ((c.not_in as unknown[]) ?? []).map(String) };
      if ('exists' in c) return { fact, op: 'exists', value: !!c.exists };
      if ('contains' in c) {
        const v = c.contains;
        return { fact, op: 'contains', value: Array.isArray(v) ? v.map(String) : [String(v)] };
      }
    }
    return { fact, op: 'equals', value: cond };
  });
}

export function rowsToWhen(rows: ConditionRow[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const r of rows) {
    if (!r.fact) continue;
    if (r.op === 'equals') out[r.fact] = r.value;
    else if (r.op === 'any_of') out[r.fact] = r.value as string[];
    else if (r.op === 'not_in') out[r.fact] = { not_in: r.value as string[] };
    else if (r.op === 'exists') out[r.fact] = { exists: !!r.value };
    else if (r.op === 'contains') {
      const v = r.value as string[];
      out[r.fact] = { contains: v.length === 1 ? v[0] : v };
    }
  }
  return out;
}

/** Problems the editor can catch before saving (the server validates again on save). */
export function rowProblems(rows: ConditionRow[]): string[] {
  const issues: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    if (!r.fact) { issues.push('missing_fact'); continue; }
    if (seen.has(r.fact)) issues.push('duplicate_fact');
    seen.add(r.fact);
    if (Array.isArray(r.value) && r.value.length === 0) issues.push('empty_list');
  }
  return [...new Set(issues)];
}

/** Next free priority after `after` (priorities must be unique). */
export function freePriority(rules: Rule[], after: number): number {
  const used = new Set(rules.map((r) => r.priority));
  let p = after + 1;
  while (used.has(p)) p += 1;
  return p;
}

export function slugify(text: string): string {
  return text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}
